// src/components/EventContextMenu.jsx
// Right-click context menu for calendar events.
// Actions vary by event type: availability, booking, proposal.
import { useEffect, useRef } from 'react';
import { Info, X, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import styles from './EventContextMenu.module.css';

/**
 * EventContextMenu
 *
 * Props:
 *   event      object           — the calendar event that was right-clicked
 *   anchor     { x, y }         — page coordinates for positioning
 *   onDetails  fn(event)        — "Details" action
 *   onCancel   fn(event)        — "Cancel" action (bookings / availability blocks)
 *   onAccept   fn(event)        — "Accept" action (proposals only)
 *   onDecline  fn(event)        — "Decline" action (proposals only)
 *   onClose    fn()             — dismiss without action
 */
export default function EventContextMenu({ event, anchor, onDetails, onCancel, onAccept, onDecline, onClose }) {
  const menuRef = useRef(null);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    }
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [onClose]);

  const style = { left: anchor.x, top: anchor.y };
  const type = event?.type;

  const isAvailability = type === 'availability';
  const isBooking = type === 'booking';
  const isProposal = type === 'proposal';

  // Only availability blocks created manually (no provider) can be removed
  const canRemove = isAvailability && !event.provider;
  const canCancel = isBooking && event.status !== 'cancelled';

  return (
    <div className={styles.menu} style={style} ref={menuRef} role="menu" aria-label="Event actions">
      <div className={styles.label}>
        {isAvailability && 'Busy time'}
        {isBooking && (event.label || 'Booking')}
        {isProposal && 'Proposal'}
      </div>

      <button
        className={styles.item}
        role="menuitem"
        onClick={() => { onDetails?.(event); onClose(); }}
      >
        <Info size={14} className={styles.itemIcon} aria-hidden="true" />
        Details
      </button>

      {isProposal && onAccept && (
        <button
          className={`${styles.item} ${styles.itemAccept}`}
          role="menuitem"
          onClick={() => { onAccept(event); onClose(); }}
        >
          <CheckCircle size={14} className={styles.itemIcon} aria-hidden="true" />
          Accept
        </button>
      )}

      {isProposal && onDecline && (
        <button
          className={`${styles.item} ${styles.itemDanger}`}
          role="menuitem"
          onClick={() => { onDecline(event); onClose(); }}
        >
          <XCircle size={14} className={styles.itemIcon} aria-hidden="true" />
          Decline
        </button>
      )}

      {canCancel && (
        <>
          <div className={styles.divider} />
          <button
            className={`${styles.item} ${styles.itemDanger}`}
            role="menuitem"
            onClick={() => { onCancel?.(event); onClose(); }}
          >
            <X size={14} className={styles.itemIcon} aria-hidden="true" />
            Cancel booking
          </button>
        </>
      )}

      {canRemove && (
        <>
          <div className={styles.divider} />
          <button
            className={`${styles.item} ${styles.itemDanger}`}
            role="menuitem"
            onClick={() => { onCancel?.(event); onClose(); }}
          >
            <X size={14} className={styles.itemIcon} aria-hidden="true" />
            Remove block
          </button>
        </>
      )}
    </div>
  );
}
